<template>
  <div class="flex flex-col gap-4 overflow-y-auto bg-white">
    <h3 class="capitalize-first text-2xl font-bold">
      {{ $t(title) }}
    </h3>
    <hr />
    <WalkmanButton
      v-model="localConfig.enabled"
      :options="enabledOptions"
      size="small"
      :edition-mode="editionMode"
    />

    <template v-if="localConfig.enabled">
      <WalkmanButton
        v-if="showTemplate"
        v-model="localConfig.template"
        name="template"
        :options="templates"
        track-by="id"
        attribute-label="name"
        :label="$t('attributes.template')"
        :edition-mode="editionMode"
      />

      <div class="flex flex-col gap-2">
        <h3 class="capitalize-first text-base font-semibold">
          {{ $t('global.deceased') }}
        </h3>

        <div class="mr-3">
          <WalkmanButton
            v-model="localConfig.dossier.photo"
            :options="pictureOptions"
            size="small"
            :label="$t('global.picture')"
            :edition-mode="editionMode"
          />
        </div>
        <div
          class="grid grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] gap-3"
        >
          <CheckBoxCardComponent
            v-model="localConfig.dossier.first_name"
            :label="$t('attributes.first_name')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />

          <CheckBoxCardComponent
            v-model="localConfig.dossier.last_name"
            :label="$t('attributes.last_name')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />

          <CheckBoxCardComponent
            v-model="localConfig.dossier.birth_date"
            :label="$t('attributes.birth_date')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />

          <CheckBoxCardComponent
            v-model="localConfig.dossier.death_date"
            :label="$t('attributes.death_date')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />

          <CheckBoxCardComponent
            v-model="localConfig.dossier.civil_status"
            :label="$t('attributes.marital_status')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />

          <CheckBoxCardComponent
            v-model="localConfig.dossier.strict_privacy"
            :label="$t('attributes.funeral_in_strict_privacy')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />
        </div>

        <SelectComponent
          v-if="
            config.template === 'standard' &&
            config.dossier.emblem !== undefined
          "
          v-model="localConfig.dossier.emblem"
          name="emblem"
          :label="$t('global.emblem')"
          :values="emblems"
          track-by="id"
          display-key="name"
        />
      </div>

      <div v-if="showEvents" class="flex flex-col gap-2">
        <h3 class="capitalize-first text-base font-semibold">
          {{ $t('global.events') }}
        </h3>

        <div
          class="grid grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] gap-3"
        >
          <CheckBoxCardComponent
            v-for="event in eventTypes"
            :key="event"
            v-model="localConfig.events[event].display"
            :label="$t(`enums.event.type.${event}`)"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />
        </div>
      </div>

      <div v-if="config.theme" class="flex flex-col gap-2">
        <h3 class="capitalize-first text-base font-semibold">
          {{ $t('global.theme') }}
        </h3>

        <div
          class="grid grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] gap-3"
        >
          <CheckBoxCardComponent
            v-model="localConfig.theme.inverted_color"
            :label="$t('global.inverted_colour')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />

          <CheckBoxCardComponent
            v-if="localConfig.theme.inverted_orientation !== undefined"
            v-model="localConfig.theme.inverted_orientation"
            :label="$t('global.inverted_orientation')"
            size="small"
            :edition-mode="editionMode"
            colorfull
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="capitalize-first text-base font-semibold">
          {{ $t('global.background') }}
        </h3>

        <WalkmanButton
          v-model="localConfig.background.type"
          :options="backgroundOptions"
          size="small"
          :edition-mode="editionMode"
        />

        <input
          v-if="localConfig.background.type === 'color'"
          v-model="localConfig.background.color"
          type="color"
          :disabled="!editionMode"
          class="block h-10 w-14 cursor-pointer rounded-lg border bg-white p-1"
        />

        <SelectComponent
          v-if="localConfig.background.type === 'video'"
          v-model="localConfig.background.video"
          name="video"
          :values="sortedVideos"
          :edition-mode="editionMode"
        />
      </div>
    </template>
  </div>
</template>

<script>
import EventType from '@/assets/enums/events/EventType'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import CheckBoxCardComponent from '@c/BaseFormComponent/CheckBoxCardComponent.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'

export default {
  name: 'ScreenConfigSection',
  components: {
    BaseShowEditInput,
    CheckBoxCardComponent,
    WalkmanButton,
    SelectComponent
  },
  props: {
    title: { type: String, required: true },
    screenKey: { type: String, required: true },
    config: { type: Object, required: true },
    editionMode: { type: Boolean, required: true },
    templates: { type: Array, required: false, default: null },
    pictureOptions: { type: Array, required: false, default: null },
    backgroundOptions: { type: Array, required: false, default: null },
    sortedVideos: { type: Array, required: false, default: null },
    emblems: { type: Array, required: false, default: null }
  },
  emits: ['update:config'],
  computed: {
    localConfig: {
      get() {
        return this.config
      },
      set(value) {
        this.$emit('update:config', value)
      }
    },
    enabledOptions() {
      return [
        { value: true, label: this.$t('global.enabled') },
        { value: false, label: this.$t('global.disabled') }
      ]
    },
    showTemplate() {
      return this.screenKey !== 'dossier_window'
    },
    showEvents() {
      return (
        this.config.events &&
        (this.screenKey === 'dossier_window' ||
          this.config.template === 'detailed')
      )
    },
    eventTypes() {
      return [
        EventType.REUNION,
        EventType.VISIT,
        EventType.BODY_RAISING,
        EventType.CEREMONY,
        EventType.CREMATION,
        EventType.BURIAL
      ]
    },
    dossierFields() {
      return [
        {
          key: 'first_name',
          label: 'attributes.first_name'
        },
        {
          key: 'last_name',
          label: 'attributes.last_name'
        },
        {
          key: 'birth_date',
          label: 'attributes.birth_date'
        },
        {
          key: 'death_date',
          label: 'attributes.death_date'
        },
        {
          key: 'civil_status',
          label: 'attributes.marital_status'
        },
        {
          key: 'strict_privacy',
          label: 'attributes.funeral_in_strict_privacy'
        }
      ].filter((field) => {
        if (field.key === 'civil_status') {
          return (
            this.screenKey === 'dossier_window' ||
            this.config.template === 'detailed'
          )
        }

        return true
      })
    }
  }
}
</script>
